import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceTypesAPI } from '../../api/serviceTypes';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './ServiceManagement.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const slugify = (input) =>
  String(input || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

const blankForm = () => ({
  name: '',
  slug: '',
  description: '',
  icon: '🧺',
  color: '#475569',
  bgColor: '#f1f5f9',
  sortOrder: 100,
  isActive: true,
});

const TypeBadge = ({ type }) => (
  <span className="sm-badge" style={{ background: type.bgColor || '#f1f5f9', color: type.color || '#475569' }}>
    {type.icon || '🧺'} {type.name}
  </span>
);

const StatusBadge = ({ active }) => (
  <span className="sm-badge" style={{
    background: active ? '#d1fae5' : '#fee2e2',
    color:      active ? '#065f46' : '#991b1b',
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ServiceTypeManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const [types, setTypes]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');

  // Create / Edit modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode]           = useState('create');
  const [editingId, setEditingId]         = useState(null);
  const [formData, setFormData]           = useState(blankForm());
  const [formErrors, setFormErrors]       = useState({});
  const [formLoading, setFormLoading]     = useState(false);

  // Deactivate / Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceTypesAPI.getAll();
      setTypes(res.data || []);
    } catch {
      showNotification('Failed to load service types', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = types.filter(t => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return t.name?.toLowerCase().includes(q)
        || t.slug?.toLowerCase().includes(q)
        || t.description?.toLowerCase().includes(q);
  });

  // ── Create ─────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    setFormData(blankForm());
    setFormErrors({});
    setShowFormModal(true);
  };

  const openEdit = (type) => {
    setFormMode('edit');
    setEditingId(type._id);
    setFormData({
      name: type.name || '',
      slug: type.slug || '',
      description: type.description || '',
      icon: type.icon || '🧺',
      color: type.color || '#475569',
      bgColor: type.bgColor || '#f1f5f9',
      sortOrder: type.sortOrder ?? 100,
      isActive: type.isActive ?? true,
      isSystem: type.isSystem ?? false,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const handleField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Auto-fill slug while creating from name (until manually edited).
      if (formMode === 'create' && field === 'name' && !prev.slugTouched) {
        next.slug = slugify(value);
      }
      if (field === 'slug') next.slugTouched = true;
      return next;
    });
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    const slug = formData.slug || slugify(formData.name);
    if (!slug) errs.slug = 'Slug is required';
    else if (!/^[a-z0-9_]+$/.test(slug)) errs.slug = 'Slug must contain only lowercase letters, digits, and underscores';
    if (formData.icon && formData.icon.length > 4) errs.icon = 'Icon should be a single emoji';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitForm = async () => {
    if (!validate()) return;
    setFormLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon.trim() || '🧺',
        color: formData.color,
        bgColor: formData.bgColor,
        sortOrder: Number(formData.sortOrder) || 100,
        isActive: formData.isActive,
      };

      if (formMode === 'create') {
        payload.slug = formData.slug || slugify(formData.name);
        await serviceTypesAPI.create(payload);
        showNotification('Service type created', 'success');
      } else {
        await serviceTypesAPI.update(editingId, payload);
        showNotification('Service type updated', 'success');
      }
      setShowFormModal(false);
      loadTypes();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Could not save service type', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Delete / Deactivate ────────────────────────────────────────────────────
  const openDelete = (type, e) => {
    e?.stopPropagation();
    setDeleteTarget(type);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.isSystem || (deleteTarget.serviceCount || 0) > 0) {
        // Soft path — deactivate.
        await serviceTypesAPI.update(deleteTarget._id, { isActive: false });
        showNotification(`"${deleteTarget.name}" deactivated`, 'success');
      } else {
        await serviceTypesAPI.delete(deleteTarget._id);
        showNotification(`"${deleteTarget.name}" deleted`, 'success');
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadTypes();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Could not remove service type', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Summary numbers ────────────────────────────────────────────────────────
  const activeCount   = types.filter(t => t.isActive).length;
  const inactiveCount = types.filter(t => !t.isActive).length;
  const systemCount   = types.filter(t => t.isSystem).length;
  const inUseCount    = types.reduce((sum, t) => sum + (t.serviceCount || 0), 0);

  return (
    <div className="ps-root">

      {/* Header */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Service Types Master</h1>
          <p className="ps-subtitle">Define the service tiers shown in the Services form and POS</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/services')}>← Back to Services</Button>
          <Button variant="primary" onClick={openCreate}>+ New Service Type</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="ps-summary-row">
        <SummaryCard icon="🧺" label="Total Types" accent="#3b82f6"
                     value={types.length}
                     sub={`${activeCount} active`} />
        <SummaryCard icon="✅" label="Active" accent="#10b981"
                     value={activeCount}
                     sub="visible in the form" />
        <SummaryCard icon="⏸️" label="Inactive" accent="#ef4444"
                     value={inactiveCount}
                     sub="hidden from new entries" />
        <SummaryCard icon="🔒" label="System" accent="#8b5cf6"
                     value={systemCount}
                     sub="cannot be deleted" />
        <SummaryCard icon="📦" label="Services Mapped" accent="#f59e0b"
                     value={inUseCount}
                     sub="across all types" />
      </div>

      {/* Content */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Filters */}
          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by name, slug or description…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="sm-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <Button variant="secondary" onClick={loadTypes}>↺</Button>
          </div>

          <div className="ps-table-meta">
            <span className="ps-count">{filtered.length} type{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="ps-loading">Loading service types…</div>
          ) : (
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Slug</th>
                    <th>Sort</th>
                    <th style={{ textAlign: 'right' }}>Services Using</th>
                    <th>Status</th>
                    <th>System</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="ps-empty">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>🧺</div>
                        No service types found
                      </td>
                    </tr>
                  ) : filtered.map(t => (
                    <tr key={t._id} className="ps-row" onClick={() => openEdit(t)}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <TypeBadge type={t} />
                        </div>
                        {t.description && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                            {t.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <code style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#334155' }}>
                          {t.slug}
                        </code>
                      </td>
                      <td style={{ color: '#64748b' }}>{t.sortOrder ?? 100}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>
                        {t.serviceCount ?? 0}
                        {(t.serviceCount ?? 0) > 0 && (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {t.activeServiceCount ?? 0} active
                          </div>
                        )}
                      </td>
                      <td><StatusBadge active={t.isActive} /></td>
                      <td>
                        {t.isSystem
                          ? <span className="sm-badge" style={{ background: '#ede9fe', color: '#5b21b6' }}>🔒 System</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="om-action-btn" onClick={() => openEdit(t)} title="Edit">✏️</button>
                          {(t.isActive || !t.isSystem) && (
                            <button
                              className="om-action-btn danger"
                              onClick={(e) => openDelete(t, e)}
                              title={t.isSystem ? 'Deactivate' : (t.serviceCount > 0 ? 'Deactivate' : 'Delete')}
                            >
                              {t.isSystem || t.serviceCount > 0 ? '⏸️' : '🗑️'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create / Edit Modal ── */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={formMode === 'create' ? 'New Service Type' : `Edit: ${formData.name}`}
      >
        <div className="ps-modal-form" style={{ paddingTop: 0 }}>

          <div className="sm-form-grid-2">
            <div className="sm-form-field">
              <label className="sm-form-label">Display Name <span className="req">*</span></label>
              <input
                className={`sm-form-input${formErrors.name ? ' error' : ''}`}
                placeholder="e.g. Dry Clean Urgent"
                value={formData.name}
                onChange={e => handleField('name', e.target.value)}
              />
              {formErrors.name && <div className="sm-field-error">{formErrors.name}</div>}
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">
                Slug <span className="req">*</span>
                {formMode === 'edit' && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                    immutable
                  </span>
                )}
              </label>
              <input
                className={`sm-form-input${formErrors.slug ? ' error' : ''}`}
                placeholder="dry_clean_urgent"
                value={formData.slug}
                onChange={e => handleField('slug', slugify(e.target.value))}
                disabled={formMode === 'edit'}
              />
              {formErrors.slug && <div className="sm-field-error">{formErrors.slug}</div>}
            </div>
          </div>

          <div className="sm-form-grid-3">
            <div className="sm-form-field">
              <label className="sm-form-label">Icon</label>
              <input
                className={`sm-form-input${formErrors.icon ? ' error' : ''}`}
                placeholder="✨"
                maxLength={4}
                value={formData.icon}
                onChange={e => handleField('icon', e.target.value)}
              />
              {formErrors.icon && <div className="sm-field-error">{formErrors.icon}</div>}
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Text Color</label>
              <input
                type="color"
                className="sm-form-input"
                style={{ padding: 2, height: 38 }}
                value={formData.color}
                onChange={e => handleField('color', e.target.value)}
              />
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Background Color</label>
              <input
                type="color"
                className="sm-form-input"
                style={{ padding: 2, height: 38 }}
                value={formData.bgColor}
                onChange={e => handleField('bgColor', e.target.value)}
              />
            </div>
          </div>

          <div className="sm-form-grid-2">
            <div className="sm-form-field">
              <label className="sm-form-label">Sort Order</label>
              <input
                type="number"
                className="sm-form-input"
                value={formData.sortOrder}
                onChange={e => handleField('sortOrder', e.target.value)}
              />
            </div>
            <div className="sm-form-field" style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
              <button
                type="button"
                className={`sm-toggle-switch${formData.isActive ? ' on' : ' off'}`}
                onClick={() => handleField('isActive', !formData.isActive)}
              >
                <div className="sm-toggle-knob" />
              </button>
              <span className="sm-toggle-label">
                {formData.isActive ? '✅ Active' : '🚫 Inactive'}
              </span>
            </div>
          </div>

          <div className="sm-form-field">
            <label className="sm-form-label">Description</label>
            <textarea
              className="sm-form-textarea"
              rows={2}
              placeholder="Optional internal note for this tier"
              value={formData.description}
              onChange={e => handleField('description', e.target.value)}
            />
          </div>

          {/* Preview */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Preview:</span>
            <TypeBadge type={formData} />
          </div>

          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={() => setShowFormModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={submitForm} disabled={formLoading}>
              {formLoading ? 'Saving…' : (formMode === 'create' ? 'Create Type' : 'Save Changes')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Delete / Deactivate Modal ── */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        title={deleteTarget?.isSystem || (deleteTarget?.serviceCount > 0)
          ? 'Deactivate Service Type'
          : 'Delete Service Type'}
      >
        {deleteTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  {deleteTarget.isSystem
                    ? `Deactivate "${deleteTarget.name}"?`
                    : (deleteTarget.serviceCount > 0
                      ? `Deactivate "${deleteTarget.name}"?`
                      : `Delete "${deleteTarget.name}"?`)}
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  {deleteTarget.isSystem
                    ? 'This is a system type — it can be hidden but never removed.'
                    : (deleteTarget.serviceCount > 0
                      ? `${deleteTarget.serviceCount} service${deleteTarget.serviceCount === 1 ? '' : 's'} still reference this type. Deactivating hides it from new entries but keeps existing services intact.`
                      : 'No services reference this type, so it will be deleted permanently.')}
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading
                  ? 'Working…'
                  : (deleteTarget.isSystem || deleteTarget.serviceCount > 0 ? 'Deactivate' : 'Delete Permanently')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ServiceTypeManagement;
